import React, { useState } from 'react';
import { Connection, Transaction, TransactionInstruction, PublicKey } from '@solana/web3.js';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useService, useServices } from '../contexts/ServiceContext';
import './DirectMessageContainer.css';

const DirectMessageContainer = ({ isOpen, onClose }) => {
    const { address: walletAddress, isConnected } = useOnboarding();
    
    // Use individual service hooks - must be called unconditionally
    const directMessageService = useService('directMessageService');
    const walletService = useService('walletService');
    const services = useServices();

    // Validate wallet for messaging
    const hasValidWallet = directMessageService && walletAddress && isConnected;

    // Create Solana connection directly (devnet)
    const connection = new Connection('https://api.devnet.solana.com', 'confirmed');
    const [recipientAddress, setRecipientAddress] = useState('');
    const [messageContent, setMessageContent] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState('');
    const [error, setError] = useState('');

    // Send direct message using DirectMessageService
    const sendMessage = async () => {
        if (!recipientAddress.trim() || !messageContent.trim()) {
            setError('Both recipient address and message content are required');
            return;
        }

        if (!walletAddress || !directMessageService) {
            setError('Wallet not connected or service unavailable');
            return;
        }

        setSending(true);
        setError('');
        setResult('');

        try {
            console.log('💬 Attempting to send message:', {
                from: walletAddress,
                to: recipientAddress,
                content: messageContent,
                hasDirectMessageService: !!directMessageService
            });

            // Use DirectMessageService
            if (!directMessageService) {
                throw new Error('Direct message service not available');
            }

            // Check if wallet service has the actual wallet connection
            console.log('💬 Wallet service debug:', {
                hasWalletService: !!walletService,
                walletServiceKeys: walletService ? Object.keys(walletService) : 'none',
                hasWalletProperty: !!walletService?.wallet,
                walletServiceType: typeof walletService
            });
            
            if (!walletService) {
                throw new Error('Wallet service not available');
            }
            
            // The walletService might not have a .wallet property, let's use it directly
            // or get the actual wallet from the service

            // Create connection for DirectMessageService
            const connection = new Connection('https://api.devnet.solana.com', 'confirmed');
            
            // Create wallet object from walletService properties
            const wallet = {
                publicKey: walletService.publicKey,
                signTransaction: walletService.provider?.signTransaction?.bind(walletService.provider),
                connected: walletService.isConnectedState,
                address: walletService.walletAddress
            };
            
            console.log('💬 Created wallet object:', {
                hasPublicKey: !!wallet.publicKey,
                hasSignTransaction: !!wallet.signTransaction,
                connected: wallet.connected,
                address: wallet.address
            });
            
            // Get updated DirectMessageService with current wallet
            const updatedDMService = services.getDirectMessageService(wallet, connection);

            const result = await updatedDMService.sendMessage(
                recipientAddress.trim(),
                messageContent.trim(),
                'text'
            );
            
            if (result.success) {
                setResult(`Message sent successfully! Transaction: ${result.transactionId}`);
                setMessageContent('');
                setRecipientAddress('');
                console.log('💬 Message sent successfully:', result.transactionId);
            } else {
                throw new Error(result.error || 'Failed to send message');
            }
            
        } catch (err) {
            console.error('Error sending message:', err);
            setError(`Failed to send message: ${err.message}`);
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    // Always show the simple send message form - bypass wallet connection check
    return (
        <div className="dm-container-overlay">
            <div className="dm-container">
                <div className="dm-container-header">
                    <h2>Direct Messages</h2>
                    <button className="dm-close-btn" onClick={onClose}>
                        ✕
                    </button>
                </div>
                <div className="dm-container-content">
                    <div className="dm-send-form">
                        <div className="dm-form-group">
                            <label htmlFor="recipientAddress">Send to (Wallet Address):</label>
                            <input
                                type="text"
                                id="recipientAddress"
                                value={recipientAddress}
                                onChange={(e) => setRecipientAddress(e.target.value)}
                                placeholder="Enter recipient wallet address"
                                className="dm-input"
                                disabled={sending}
                            />
                        </div>
                        
                        <div className="dm-form-group">
                            <label htmlFor="messageContent">Message:</label>
                            <textarea
                                id="messageContent"
                                value={messageContent}
                                onChange={(e) => setMessageContent(e.target.value)}
                                placeholder="Enter your message"
                                className="dm-textarea"
                                rows={4}
                                disabled={sending}
                            />
                        </div>
                        
                        <button 
                            onClick={sendMessage}
                            disabled={sending || !recipientAddress.trim() || !messageContent.trim()}
                            className="dm-send-btn"
                        >
                            {sending ? 'Sending...' : 'Send Message'}
                        </button>
                        
                        {error && (
                            <div className="dm-error">
                                {error}
                            </div>
                        )}
                        
                        {result && (
                            <div className="dm-success">
                                {result}
                            </div>
                        )}
                        
                        <div className="dm-info">
                            <p><strong>From:</strong> {walletAddress || 'Not connected'}</p>
                            <p><small>Messages are stored on Solana blockchain using memo program</small></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DirectMessageContainer;
